'use client';

import { useCallback, useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { StructuredTargetVo } from 'models/milestones';

import type { StructuredTargetMap } from './structuredTargetCache';
import { emptyStructuredTargetMap, mergeIntoNormalizedTargets, structuredTargetKeys } from './structuredTargetCache';

/**
 * Keeps the normalized map alive for mounted readers, including fresh cached query hits.
 *
 * Why an observer is needed at all: `Query.scheduleGc()` runs from the constructor, from
 * `removeObserver` and after a fetch — never from `setData`. So the map's collection timer is
 * armed once, when the entry is first built, and no amount of later `mergeIntoNormalizedTargets`
 * writes pushes it out. Nothing observed `structuredTargetKeys.normalized` on a chart page
 * (`useStructuredTargetFromCache`, the only other observer, is used by the power-rankings crest
 * and the market-tile team record), so the map was removed on schedule while the chart was still
 * reading it imperatively — turning `MICH`/`IOWA` back into `Michigan`/`Iowa` and dropping the
 * team palettes. An observer clears that timer (`Query.addObserver` -> `clearGcTimeout`).
 *
 * WHAT THIS COSTS. The normalized map is one object holding every structured target merged during
 * the session, and this pins it for as long as ANY reader is mounted. On a trading surface that is
 * effectively the whole session: the event chart, the order panel and the hub grids all mount a
 * structured-target query. Collection still happens, but only once the last reader unmounts, so
 * the `CLIENT_GC_TIME_MS` window in `app/get-query-client.ts` — deliberately halved to 2 minutes
 * on <=4 GB devices to reclaim query data sooner — no longer bounds this cache in practice. The
 * payload is immutable team/player metadata (a few hundred bytes per entry, low thousands of
 * entries in a long session), which is why that trade is acceptable here and would not be for a
 * cache of market or price data.
 *
 * Known gap: retention lapses while `data` is empty, because an observer is only subscribed when
 * there is something to retain. In practice the reader's own query holds its data for as long as
 * it is mounted, so this only bites if that data goes `undefined` for longer than `gcTime`.
 */
export function useRetainStructuredTargets<T>(data: T | undefined, toTargets: (data: T) => StructuredTargetVo[]): void {
    const queryClient = useQueryClient();
    const targets = useMemo(() => (data === undefined ? [] : toTargets(data)), [data, toTargets]);
    const select = useCallback((map: StructuredTargetMap) => targets.every((target) => !!map[target.id]), [targets]);
    const { data: hasTargets } = useQuery({
        queryKey: structuredTargetKeys.normalized,
        queryFn: emptyStructuredTargetMap,
        enabled: false,
        subscribed: targets.length > 0,
        select,
        notifyOnChangeProps: ['data'],
    });

    // A fresh by-id/all query can outlive the normalized map. Restore missing targets
    // after mount, and notify the reader so its imperative chart selectors recompute.
    // Fills gaps only — a caller holding a fresher copy of a target it already merged
    // (a BFF payload, say) still has to seed it explicitly.
    useEffect(() => {
        if (!hasTargets) mergeIntoNormalizedTargets(queryClient, targets);
    }, [queryClient, targets, hasTargets]);
}
